import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BlogService } from '../services/blog.service';
import { BlogCategory, BlogListItem } from './blog.models';
import { SeoService } from '../services/seo.service';
import { BreadcrumbsService } from '../services/breadcrumbs.service';

@Component({
  selector: 'app-blog-list',
  templateUrl: './blog-list.component.html',
  styleUrls: ['./blog-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogListComponent implements OnInit, AfterViewInit, OnDestroy {
  blogs: BlogListItem[] = [];
  categories: BlogCategory[] = [];
  activeCategory: string | null = null;
  readonly searchControl = new FormControl('');

  isLoading = false;
  isLoadingMore = false;
  hasMore = false;
  errorMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private observer?: IntersectionObserver;
  private nextCursor: string | null = null;
  private currentQuery = '';
  private readonly pageSize = 10;

  @ViewChild('loadMoreSentinel', { static: false })
  loadMoreSentinel?: ElementRef<HTMLDivElement>;

  constructor(
    private readonly blogService: BlogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly seo: SeoService,
    private readonly breadcrumbs: BreadcrumbsService,
    private readonly zone: NgZone
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.setupSeo();
    this.loadCategories();
    this.setupSearchListener();
    this.fetchBlogs();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.observer?.disconnect();
  }

  onSelectCategory(category: BlogCategory | null): void {
    const slug = category?.slug ?? null;
    if (this.activeCategory === slug) {
      return;
    }
    this.activeCategory = slug;
    this.resetAndFetch();
  }

  onLoadMore(): void {
    if (!this.hasMore || this.isLoading || this.isLoadingMore) {
      return;
    }
    this.fetchBlogs({ append: true, cursor: this.nextCursor ?? undefined });
  }

  retry(): void {
    this.resetAndFetch();
  }

  trackBySlug(_: number, item: BlogListItem): string {
    return item.slug;
  }

  private setupSeo(): void {
    const canonical = this.seo.generateCanonical('/stories');
    this.seo.updateSeo({
      title: 'Blauwtand | Lees onze ervaringen in IT recruitment',
      description:
        'Ontdek verhalen, inzichten & praktische tips over IT professionals en de arbeidsmarkt. We vertellen je graag meer over al onze ervaringen in IT recruitment.',
      canonicalUrl: canonical,
      ogType: 'website',
    });
    this.breadcrumbs.setCurrentLeafLabel('Stories');
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.currentQuery = (value ?? '').trim();
        this.resetAndFetch();
      });
  }

  private loadCategories(): void {
    this.blogService
      .categories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categories) => {
          this.categories = categories.sort((a, b) =>
            a.name.localeCompare(b.name, 'nl-NL')
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.categories = [];
          this.cdr.markForCheck();
        },
      });
  }

  private resetAndFetch(): void {
    this.blogs = [];
    this.nextCursor = null;
    this.hasMore = false;
    this.fetchBlogs();
  }

  private fetchBlogs(
    options: { append?: boolean; cursor?: string } = {}
  ): void {
    const { append = false, cursor } = options;
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
    }
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.blogService
      .list({
        query: this.currentQuery,
        category: this.activeCategory,
        cursor,
        limit: this.pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.blogs = append
            ? [...this.blogs, ...(response.items ?? [])]
            : [...(response.items ?? [])];
          this.nextCursor = response.nextCursor ?? null;
          this.hasMore = Boolean(this.nextCursor);
          this.isLoading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage =
            'De blogartikelen konden niet worden geladen. Probeer het later opnieuw.';
          this.isLoading = false;
          this.isLoadingMore = false;
          this.hasMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private setupIntersectionObserver(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }
    if (!this.loadMoreSentinel) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.zone.run(() => this.onLoadMore());
            }
          });
        },
        {
          rootMargin: '0px 0px 280px 0px',
          threshold: 0.1,
        }
      );
      if (this.loadMoreSentinel?.nativeElement) {
        this.observer.observe(this.loadMoreSentinel.nativeElement);
      }
    });
  }
}
